import React from 'react';
import { useSelector } from 'react-redux';
import Markdown from 'markdown-to-jsx';
import { Typography } from '@drone-js/core';
import { ContentType } from 'gql/autogen/ContentType';
import { MarkdownListStyled } from 'component/common/AgodaMarkdown/MarkdownList.styled';
// eslint-disable-next-line import/no-restricted-paths
import { selectFieldContentTypesFromContentDetailState } from 'component/page/detail/store/detail.selectors';
import { ActivityTextLink, ActivityTextLinkProps } from 'component/common/ActivityTextLink/ActivityTextLink';

interface Props {
    input: string;
    inline?: boolean;
    skipHtml?: boolean;
    overrides?: {};
    className?: string;
    dataElementName?: string;
    isActivityDescription?: boolean;
    disableLinkFocus?: boolean;
}

interface UnicodeDictionary {
    [key: string]: string;
}

export const handleHtmlCode = (inputText: string, namedCodesToUnicode: UnicodeDictionary): string => {
    // All the logic inside this function is copied from the source code of markdown-to-jsx. Please remove this function when the issue is fixed.
    const HTML_CHAR_CODE_R = /&([a-z0-9]+|#[0-9]{1,6}|#x[0-9a-fA-F]{1,6});/gi;
    return inputText.replace(HTML_CHAR_CODE_R, (full, inner: string) => {
        return namedCodesToUnicode[inner] ? namedCodesToUnicode[inner] : full;
    });
};

const AgodaMarkdown: React.FC<Props> = (props) => {
    const defaultMarkdownFormat = {
        p: { component: Typography, props: { mb: '12' } },
        ul: { component: MarkdownListStyled },
        h3: { component: Typography, props: { variant: 'body-s-strong', my: '8' } },
        a: {
            component: ActivityTextLink,
            props: { target: '_blank', typography: { textColor: 'product.primary' }, disableLinkFocus: props.disableLinkFocus } as ActivityTextLinkProps,
        },
        img: { props: { width: '100%' } },
    };

    const fieldContentType = useSelector(selectFieldContentTypesFromContentDetailState);
    const inline = props.inline || false;
    const overrides = props.overrides || defaultMarkdownFormat;
    const options = {
        disableParsingRawHTML: props.skipHtml || false,
        forceWrapper: true,
        wrapper: (inline ? (<span />).type : (<div />).type) as React.ElementType,
        overrides,
        // Somehow namedCodesToUnicode option in markdown-to-jsx is not working, so we have to do it manually.
        // namedCodesToUnicode : { '#39': '\u0027', '#34': '\u0022' },
    };
    const namedCodesToUnicode = { '#39': '\u0027', '#34': '\u0022' };
    const markdownContent = (
        <Markdown className={props.className} data-testid={props.dataElementName} options={options}>
            {handleHtmlCode(props.input.trim(), namedCodesToUnicode)}
        </Markdown>
    );

    if (!fieldContentType || fieldContentType.length === 0) {
        return markdownContent;
    }

    return (
        <>
            {fieldContentType.map(
                // To improve ACT-3635 FieldType key mapper
                (content, key) =>
                    props.isActivityDescription && content.contentType === ContentType.PLAIN_TEXT ? (
                        <Typography key={key} data-testid={props.dataElementName}>
                            {props.input}
                        </Typography>
                    ) : (
                        markdownContent
                    ),
            )}
        </>
    );
};
AgodaMarkdown.displayName = 'AgodaMarkdown';

export default AgodaMarkdown;
